import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Loader2, AlertCircle } from 'lucide-react';

interface RoasData {
  summary: {
    total_spend_eur: number;
    total_revenue_eur: number;
    roas: number;
    orders: number;
    aov_eur: number;
  };
  daily_trend: Array<{
    day: string;
    roas: number;
    spend: number;
    revenue: number;
  }>;
}

interface FunnelData {
  stage: string;
  value: number;
}

interface OptimizationData {
  suggestion_id: string;
  type: string;
  title: string;
  reason: string;
  action_text: string;
  highlight: string;
  color: string;
}

export default function Analytics() {
  const { data: roasData, isLoading: roasLoading, isError: roasError } = useQuery<RoasData>({
    queryKey: ['analytics', 'roas'],
    queryFn: async () => {
      const response = await api.get('/analytics/roas');
      return response.data;
    },
  });

  const { data: funnelData, isLoading: funnelLoading } = useQuery<FunnelData[]>({
    queryKey: ['analytics', 'funnel'],
    queryFn: async () => {
      const response = await api.get('/analytics/funnel');
      return response.data;
    },
  });

  const { data: optimizations, isLoading: optLoading } = useQuery<OptimizationData[]>({
    queryKey: ['analytics', 'optimization'],
    queryFn: async () => {
      const response = await api.get('/analytics/optimization');
      return response.data;
    },
  });

  const isLoading = roasLoading || funnelLoading || optLoading;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Loader2 className="h-8 w-8 text-primary-500 animate-spin" />
        <p className="text-gray-400">Loading analytics data...</p>
      </div>
    );
  }

  if (roasError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4 text-red-500">
        <AlertCircle className="h-8 w-8" />
        <p>Failed to load analytics data. Please check if the API is running.</p>
      </div>
    );
  }

  if (!roasData || !roasData.summary) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-gray-400">No analytics data available.</p>
      </div>
    );
  }

  const { summary = { roas: 0, total_spend_eur: 0, total_revenue_eur: 0, orders: 0, aov_eur: 0 }, daily_trend = [] } = roasData;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Analytics</h1>
        <p className="text-gray-400 mt-1">Performance insights and optimization</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <p className="text-gray-400 text-sm">Total ROAS</p>
          <p className="text-3xl font-bold text-white mt-2">{summary.roas}x</p>
          <p className="text-gray-500 text-sm mt-1">Last 7 days</p>
        </div>
        <div className="card">
          <p className="text-gray-400 text-sm">Ad Spend</p>
          <p className="text-3xl font-bold text-white mt-2">€{summary.total_spend_eur.toLocaleString()}</p>
          <p className="text-gray-500 text-sm mt-1">Last 7 days</p>
        </div>
        <div className="card">
          <p className="text-gray-400 text-sm">Revenue</p>
          <p className="text-3xl font-bold text-white mt-2">€{summary.total_revenue_eur.toLocaleString()}</p>
          <p className="text-gray-500 text-sm mt-1">Last 7 days</p>
        </div>
        <div className="card">
          <p className="text-gray-400 text-sm">Orders</p>
          <p className="text-3xl font-bold text-white mt-2">{summary.orders}</p>
          <p className="text-gray-500 text-sm mt-1">AOV: €{summary.aov_eur}</p>
        </div>
      </div>

      {/* ROAS Chart */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">ROAS Trend (Last 7 Days)</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={daily_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="day" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
              />
              <Line type="monotone" dataKey="roas" stroke="#0ea5e9" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversion Funnel */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Conversion Funnel</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnelData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis type="number" stroke="#94a3b8" />
                <YAxis dataKey="stage" type="category" stroke="#94a3b8" width={100} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px' }}
                />
                <Bar dataKey="value" fill="#0ea5e9" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recommendations */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">AI Recommendations</h2>
          <div className="space-y-4">
            {optimizations?.map((opt) => (
              <div 
                key={opt.suggestion_id} 
                className={`p-4 bg-${opt.color}-500/10 border border-${opt.color}-500/30 rounded-lg`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-white">{opt.title}</h4>
                    <p className="text-sm text-gray-400 mt-1">{opt.reason}</p>
                  </div>
                  <span className={`text-${opt.color}-500 text-sm font-medium`}>{opt.highlight}</span>
                </div>
                <div className="flex justify-end mt-3">
                  <button className={`btn-${opt.color === 'blue' || opt.color === 'green' ? 'primary' : 'secondary'} text-sm`}>
                    {opt.action_text}
                  </button>
                </div>
              </div>
            ))}
            
            {optimizations?.length === 0 && (
              <p className="text-center text-gray-500 py-4">No new recommendations right now.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
